import { ClinicianVendorEnumProto_Value } from "@noom/noom-contracts/noom_contracts/healthcare/model/clinician_vendor";
import { getPatientIntakeSessionStorage } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/usePatientIntakeSessionStorage";
import { getNoomSessionStorage } from "@utils/noomSessionStorage";
import {
  ClinicalIntakeAnswerMode,
  IN_APP_TASK_PATIENT_CENTER_REFERRER,
  SessionStorageKey,
} from "@utils/constants";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import { isB2BPatient } from "./b2bUtils";

// used exclusively for users coming through an emailed URL
export const isCorrectUser = (surveyAnswers: SurveyAnswersState): boolean =>
  surveyAnswers?.confirmUser?.[0] === "yes" ||
  surveyAnswers?.patientIntakeConfirmUser?.[0] === "yes";

export const isPrimarySubscriber = (
  surveyAnswers: SurveyAnswersState
): boolean => surveyAnswers?.isPrimarySubscriber?.[0] === "yes";

// SteadyMD Redirector Helpers. Here are handlers mostly used by the new
// telehealth EMR survey flow.
export const isCoveredByInsurance = (): boolean =>
  getPatientIntakeSessionStorage().insuranceCoverage === "yes";

export const isPrimarySubscriberSessionStorage = (): boolean =>
  getPatientIntakeSessionStorage().insuranceIsPrimarySubscriber === "yes";

export const getLabsStatus = (): string =>
  getPatientIntakeSessionStorage().labsStatus;

export const isPatientTakingMedication = (): boolean =>
  getPatientIntakeSessionStorage().useMedications === "yes";

export const getPatientInformationChanged = (): boolean =>
  getPatientIntakeSessionStorage().patientInformationChanged;

export const isSingleModuleSurvey = (): boolean =>
  getNoomSessionStorage(SessionStorageKey.IntakeAnswerMode) ===
  ClinicalIntakeAnswerMode.singleModuleOnly;

export const isInAppTaskCardSurvey = (): boolean =>
  getNoomSessionStorage(SessionStorageKey.IntakeAnswerMode) ===
  ClinicalIntakeAnswerMode.inAppTaskCard;

export const isVideoConsultSchedulingSurvey = () =>
  !!getNoomSessionStorage(SessionStorageKey.isVideoVisitSurvey);

export const isFullIntakeSurvey = (): boolean => {
  const intakeAnswerMode = getNoomSessionStorage(
    SessionStorageKey.IntakeAnswerMode
  );
  // Assume full intake mode if no session storage set
  return (
    !intakeAnswerMode ||
    intakeAnswerMode === ClinicalIntakeAnswerMode.allModules
  );
};

export const shouldScheduleVideoVisit = (): boolean => {
  // Users should go to the video scheduler if
  // 1. They skip lab ordering (labStatus = "SKIP")
  // 2. They have actually uploaded a lab document [labStatus = UPLOAD]
  // 3. They are a B2B user. B2B users should get into appointments as
  //    soon as possible.
  //
  // A special exclusion exists in the following circumstance
  // e1. They acknowledge they have labs (hasLabs = true) but then skip
  // uploading them. In this case, we will email them to upload
  // the labs at a later time and do not want them to schedule.
  return !getPatientIntakeSessionStorage().hasOrderedLabs || isB2BPatient();
};

export const shouldContinuePastMedicalIntakeEnd = () => {
  // The "next" or "continue" button should only be shown if the user is a
  // new user completing the holistic intake experience in the buyflow. At
  // which point this "next" or "continue" button will send them to
  // noom-premium
  //
  // The logic is a little confusing because the return is used as the "disable"
  // value. So, another way to think of this is "Don't disable if either
  // isTelehealthBuyflow or otherwise not in the "single module mode" which
  // is flipped on if users are completing a single sub-section via an emailed
  // link and we don't string the sections together like usual.
  return (
    getNoomSessionStorage(SessionStorageKey.IntakeAnswerMode) ===
      ClinicalIntakeAnswerMode.singleModuleOnly ||
    !getNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow)
  );
};

export const skipIntakeReview = (
  surveyAnswers: SurveyAnswersState
): boolean => {
  return surveyAnswers.reviewIntake?.includes("no");
};

export const hasCompletedRecentLabWork = (
  surveyAnswers: SurveyAnswersState
): boolean => {
  return surveyAnswers.recentLabs?.includes("yes");
};

export const skippedSearchForPharmacyQuestion = () =>
  !getPatientIntakeSessionStorage().searchPharmacyPostalCode &&
  !getPatientIntakeSessionStorage().searchPharmacyName;

export const referredFromNoomMedOffer = () => {
  return (
    new URLSearchParams(window.location.search).get("fromNoomMedOffer") ===
    "true"
  );
};

export const shouldShowOrderLabScreens = () =>
  // in the post-purchase or email intakes we still don't want to show
  // the order labs screen
  isB2BPatient() || isInAppTaskCardSurvey();

export const returnToPatientCenterAfterLabsTask = () => {
  return (
    getNoomSessionStorage(SessionStorageKey.telehealthInAppTaskReferrer) ===
    IN_APP_TASK_PATIENT_CENTER_REFERRER
  );
};

export const isWhiteLabelPatientIntakeSurvey = () =>
  window.location.href?.includes("whiteLabelPatientIntake");

// TODO(MM-446 / multi-med): isMdiIntakeSurvey and isBelugaIntakeSurvey read clinicianVendor from
// the URL query param set by goto.whiteLabelPatientIntake. This ties intake rendering decisions to
// a single vendor string, which breaks for users with multiple active treatments. These functions
// should be replaced with treatment-backed lookups (e.g. from useNonTerminalTreatments) so intake
// routing is driven by the full set of user treatments rather than a URL param.
export const isMdiIntakeSurvey = () => {
  const clinicianVendor = new URLSearchParams(window.location.search).get(
    "clinicianVendor"
  ) as ClinicianVendorEnumProto_Value;

  return (
    // TODO(Denis): remove 1st condition after the HS updates the URL generation to include `clinicianVendor`
    window.location.href?.includes("mdiPatientIntake") ||
    (isWhiteLabelPatientIntakeSurvey() &&
      ["MD_INTEGRATIONS", "MD_INTEGRATIONS_OBGYN"].includes(clinicianVendor))
  );
};

export const isBelugaIntakeSurvey = () => {
  const clinicianVendor = new URLSearchParams(window.location.search).get(
    "clinicianVendor"
  ) as ClinicianVendorEnumProto_Value;

  return isWhiteLabelPatientIntakeSurvey() && clinicianVendor === "BELUGA";
};

export const hasInAppSurveyModeQueryParam = () => {
  const intakeModeParam = new URLSearchParams(window.location.search).get(
    "surveyMode"
  );

  return intakeModeParam === "inAppTask";
};

export const isNoomMedUpgradeFlow = () => {
  return !!getNoomSessionStorage(SessionStorageKey.isTelehealthUpgradeFlow);
};
